import React from "react";
import DelegationForm from "../DelegationForm/DelegationForm";
import * as entryRightsActions from "../../store/actions/entryRights.actions";
import { ParkingRightsRequestModel } from "../../models/ParkingRightsRequestModel";
import { DelegationFormDetails, DelegeeWithPmcs } from "../DelegationForm/DelegationFormDetails";
import { InviteDelegeeRequestModel } from "../../models/InvitePedestrianRightRequestModel";
import { ParkingProductModel, ParkingProductType } from "../../models/ParkingProductModel";
import { DataType } from "../../store/reducers/parking-products";
import {
    EntryRightDelegeeDetails,
    UpdateEntryRightDelegeeModel,
} from "../../api/entryRights.api";
import { useAppDispatch, useAppSelector } from "../../store";
import { isNil } from "lodash";
import useFleetManagerContext from "../../context/hooks/useFleetManagerContext";
import { createSelector } from "@reduxjs/toolkit";
import { RootReducer } from "../../store/reducers";
import { useAppContext } from "../../context/hooks";
import * as parkingProductsThunks from "../../store/actions/parkingProducts.thunks";
import parkingProductsActions from "../../store/actions/parkingProducts.actions";

interface IPedestrianProps {
    onClose: () => void;
    refreshHandler?: () => void;
    delegationMode: EntryRightFormMode;
}

export type EntryRightFormMode = "pedestrian" | "parkingRights";


function createInviteModel(
    parkingRights: ParkingRightsRequestModel,
): InviteDelegeeRequestModel {
    return {
        customerId: parkingRights.seasonTicketOwnerCrmId,
        name: parkingRights.name,
        email: parkingRights.email,
        products: parkingRights.products,
    };
}

function createUpdateModel(
    customerId: string,
    parkingRights: ParkingRightsRequestModel,
    delegeeDetails: EntryRightDelegeeDetails
): UpdateEntryRightDelegeeModel {
    return {
        customerId,
        name: parkingRights.name || "",
        pmcs: parkingRights.products,
        delegeeId: delegeeDetails.id,
        sendRevocationConfirmationEmail: parkingRights.sendRevocationConfirmationEmail
    };
}

function mapToDelegeeWithPmcs(stateDelegee: EntryRightDelegeeDetails): DelegeeWithPmcs {
    return {
        id: stateDelegee.id,
        emailAddress: stateDelegee.delegeeEmail,
        name: stateDelegee.delegeeName,
        products: stateDelegee.products
    };
}

const selectParkingProducts = createSelector(
    [
        (state: RootReducer) => state.parkingProducts.parkingProducts,
        (_, isEditMode: boolean) => isEditMode,
        (_, __: boolean, delegationMode: EntryRightFormMode) => delegationMode,
    ],
    (parkingRightProducts, isEditMode, delegationMode): DataType<ParkingProductModel[]> => {
        if (isEditMode) {
            return {
                loading: parkingRightProducts.loading,
                data: parkingRightProducts.data,
                error: parkingRightProducts.error,
            };
        }

        const productType = delegationMode === "parkingRights"
            ? ParkingProductType.ParkingRights
            : ParkingProductType.PedestrianAccess;

        if (delegationMode === "parkingRights") {
            return {
                loading: parkingRightProducts.loading,
                data: parkingRightProducts.data?.filter((product) => product.productType === productType),
                error: parkingRightProducts.error,
            };
        }

        return {
            loading: parkingRightProducts.loading,
            data: parkingRightProducts.data?.filter((product) => product.productType === productType),
            error: parkingRightProducts.error,
        };
    }
);

const selectDelegeeWithPmcs = createSelector(
    (state: RootReducer) => state.entryRights.delegeeDetails,
    (stateDelegee): DataType<DelegeeWithPmcs> => {
        return {
            loading: stateDelegee.loading,
            data: isNil(stateDelegee.data) ? null : mapToDelegeeWithPmcs(stateDelegee.data),
            error: stateDelegee.error,
        };
    }
);

function selectError(state: RootReducer) {
    const err = state.entryRights.updateState.error || state.entryRights.error;
    if (!err) {
        return null;
    }
    return err;
}

function selectLoading(state: RootReducer): boolean {
    return state.entryRights.loading || state.entryRights.updateState.loading;
}

// Block when either the entry rights or the parking rights bulk import is still running.
function selectIsBulkImportCompleted(state: RootReducer): boolean {
    return state.entryRights.isBulkImportCompleted && state.parkingProducts.isBulkImportCompleted;
}

const selectDelegationFormDetails = createSelector(
    // these deps end up in useEffect dep array inside ParkingRightsGrid, so I need to memoize them to not trigger that useEffect...
    // Also better performance maybe
    [selectParkingProducts, selectDelegeeWithPmcs, selectError, selectLoading, selectIsBulkImportCompleted],
    (parkingProducts, delegeeDetails, error, loading, isBulkImportCompleted): DelegationFormDetails => {
        return {
            delegeeDetails,
            parkingProducts,
            error: error,
            isBulkImportCompleted: isBulkImportCompleted,
            loading,
        };
    }
);

const EntryRightForm: React.FC<IPedestrianProps> = (props) => {
    const dispatch = useAppDispatch();
    const pedestrianRights = useAppSelector((s) => s.entryRights);
    const { fleetManagerState } = useFleetManagerContext();
    const {
        appState: {
            user: { seasonTicketOwnerCrmId: customerId },
        },
    } = useAppContext();
    const isParkingRightsMode = props.delegationMode === "parkingRights";
    const refreshHandler = props.refreshHandler ?? (() => { });

    const inviteDelegee = async (
        parkingRights: ParkingRightsRequestModel,
        callback?: () => void
    ) => {
        const request = createInviteModel(parkingRights);
        const inviteResult = await dispatch(entryRightsActions.inviteActionThunk(request)).unwrap();
        if (inviteResult.ok) {
            callback?.();
        }
    };

    const updateDelegee = async (
        parkingRights: ParkingRightsRequestModel,
        callback?: () => void
    ) => {
        const request = createUpdateModel(
            customerId!,
            parkingRights,
            pedestrianRights.delegeeDetails.data!
        );
        const updateResult = await dispatch(
            entryRightsActions.updateEntryRightDelegeeActionThunk({model: request, delegeePmcIds: pedestrianRights.delegeeDetails.data!.products.map(p => p.pmcId)})
        ).unwrap();
        if (updateResult.ok) {
            callback?.();
        }
    };

    const getProducts = (crmId: string, language: string) => {
         dispatch(parkingProductsThunks.getParkingProducts({ seasonTicketOwnerCrmId: crmId, location: language }));
    };

    const getDelegeeDetails = (id: string, language: string) => {
        dispatch(entryRightsActions.getEntryRightDelegeeDetailsActionThunk({ id, language }));
    };

    const clearDelegeeDetails = () => {
        dispatch(entryRightsActions.clearDelegeeDetailsAction());
    };

    const handleDelete = () => {
        dispatch(entryRightsActions.toggleShowDeleteEntryRightsDelegeesAction());
    };

    const disposeError = () => {
        dispatch(entryRightsActions.disposeErrorAction());
    };

    const resetBulkImportProgress = () => {
        dispatch(entryRightsActions.resetBulkImportProgressAction());
    };

    const getBulkImportStatus = (seasonTicketOwnerCrmId: string) => {
        return Promise.all([
            dispatch(entryRightsActions.getBulkImportStatusActions(seasonTicketOwnerCrmId)),
            dispatch(parkingProductsActions.getBulkImportStatus(seasonTicketOwnerCrmId)),
        ]);
    };

    const formDetails = useAppSelector((state) =>
        selectDelegationFormDetails(state, fleetManagerState.isEditDelegeeMode, props.delegationMode)
    );
    return (
        <div>
            <DelegationForm
                onClose={props.onClose}
                refreshHandler={refreshHandler}
                getProducts={getProducts}
                inviteDelegee={inviteDelegee}
                updateDelegation={updateDelegee}
                getDelegeeDetails={getDelegeeDetails}
                clearDelegeeDetails={clearDelegeeDetails}
                onDelete={handleDelete}
                resetBulkImportProgress={resetBulkImportProgress}
                getBulkImportStatus={getBulkImportStatus}
                disposeError={disposeError}
                isEditMode={fleetManagerState.isEditDelegeeMode}
                translationContextName={isParkingRightsMode ? "parkingRightsForm" : "pedestrianForm"}
                disableMultipleOption={false}
                delegationFormDetails={formDetails}
                shouldBlockForSingle={true}
            />
        </div>
    );
};

export default EntryRightForm;
